import { useState } from 'react';

import {
  AdornedInput,
  Box,
  ButtonGroup,
  FormField,
  FormHelp,
  IconButton,
  Label,
  Select,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import { IconCopyFilled, IconMinusCircleFilled } from '@zillow/constellation-icons';

import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { getSquareFeetError, isDuplicateUnitNumber, MAX_UNIT_NUMBER_LENGTH, UnitRow } from '../../../utils';

import { bathOptions, bedOptions } from '../../../constants';

import { DescriptionPlaceholder } from '../../../index';

import type { ReactNode } from 'react';

type EditableUnitGridProps = {
  rows: Array<UnitRow>;
  onChange: (rows: Array<UnitRow>) => void;
  existingUnitNumbers: Array<string>;
  hasSubmitted: boolean;
  maxUnits: number;
  minRows?: number;
  renderRowActions?: (row: UnitRow, isRowValid: boolean) => ReactNode;
};

const EditableUnitGrid = ({
  rows,
  onChange,
  existingUnitNumbers,
  hasSubmitted,
  maxUnits,
  minRows = 1,
  renderRowActions,
}: EditableUnitGridProps) => {
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null);

  const handleRemoveRow = (id: string) => {
    if (rows.length <= minRows) return;
    onChange(rows.filter((row) => row.id !== id));
  };

  const handleCopyRow = (id: string) => {
    if (rows.length >= maxUnits) return;
    const source = rows.find((row) => row.id === id);
    if (!source) return;
    const copy: UnitRow = { ...source, id: crypto.randomUUID() };
    const index = rows.findIndex((row) => row.id === id);
    const next = [...rows];
    next.splice(index + 1, 0, copy);
    onChange(next);
  };

  const handleFieldChange = (id: string, field: keyof Omit<UnitRow, 'id'>, value: string) => {
    onChange(rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
      <Box
        css={{
          display: { base: 'none', md: 'grid' },
          gridTemplateColumns: '1fr 1fr 1fr 1fr 48px 48px',
          columnGap: 'default',
          alignItems: 'end',
        }}
      >
        <Text
          aria-hidden="true"
          textStyle="body-sm-bold"
          css={{
            _after: { content: '"*" / ""', color: 'text.feedback.critical.hero', marginInlineStart: 'micro.default' },
          }}
        >
          Unit number
        </Text>
        <Text
          aria-hidden="true"
          textStyle="body-sm-bold"
          css={{
            _after: { content: '"*" / ""', color: 'text.feedback.critical.hero', marginInlineStart: 'micro.default' },
          }}
        >
          Beds
        </Text>
        <Text
          aria-hidden="true"
          textStyle="body-sm-bold"
          css={{
            _after: { content: '"*" / ""', color: 'text.feedback.critical.hero', marginInlineStart: 'micro.default' },
          }}
        >
          Baths
        </Text>
        <Text
          aria-hidden="true"
          textStyle="body-sm-bold"
          css={{
            _after: {
              content: '"(optional)" / ""',
              color: 'text.subtle',
              fontWeight: 'normal',
              marginInlineStart: 'micro.default',
            },
          }}
        >
          Size
        </Text>
        <Text aria-hidden="true" textStyle="body-sm-bold" css={{ gridColumn: 'span 2' }}>
          Options
        </Text>
      </Box>

      {rows.map((row) => {
        const isFocused = focusedRowId === row.id;
        const isOverLimit = row.unitNumber.length > MAX_UNIT_NUMBER_LENGTH;
        const isDuplicate = isDuplicateUnitNumber(row.id, row.unitNumber, rows, existingUnitNumbers);
        const isEmpty = hasSubmitted && !row.unitNumber.trim();
        const hasUnitNumberError = isEmpty || isDuplicate || isOverLimit;
        const isRowValid = !!row.unitNumber.trim() && !!row.beds && !!row.baths && !isOverLimit && !isDuplicate;
        const showBedsError = hasSubmitted && !row.beds;
        const showBathsError = hasSubmitted && !row.baths;
        const sizeError = getSquareFeetError(row.size);
        const sizeValue = row.size === '' ? null : Number(row.size);
        const showLargeSizeWarning = !sizeError && sizeValue !== null && !Number.isNaN(sizeValue) && sizeValue >= 20000;
        const sizeFieldMessage =
          sizeError ??
          (showLargeSizeWarning
            ? 'Square footage values of 20,000 or more are unusually large. Verify this is correct.'
            : null);

        const unitNumberDescription = (() => {
          if (isEmpty) return <FormHelp>Unit number can’t be empty</FormHelp>;
          if (isDuplicate) return <FormHelp>This unit already exists</FormHelp>;
          if (isFocused || isOverLimit) {
            return (
              <CharacterCountFormHelp
                currentLength={row.unitNumber.length}
                maxLength={MAX_UNIT_NUMBER_LENGTH}
                isFocused={isFocused}
                label="Unit number"
                helperText="Enter unit number (e.g., 1A, 201)"
              />
            );
          }
          return <DescriptionPlaceholder />;
        })();

        return (
          <Box
            key={row.id}
            data-testid="unit-row"
            css={{
              display: 'grid',
              gridTemplateColumns: { base: '1fr 1fr', md: '1fr 1fr 1fr 1fr 48px 48px' },
              columnGap: 'default',
              alignItems: 'start',
            }}
          >
            <Box css={{ gridColumn: { base: '1 / span 2', md: 'auto' } }}>
              <FormField
                required={true}
                error={hasUnitNumberError}
                label={<Label css={{ md: { srOnly: true } }}>Unit number</Label>}
                control={
                  <AdornedInput
                    startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                    input={
                      <AdornedInput.Input
                        value={row.unitNumber}
                        onChange={(e) => handleFieldChange(row.id, 'unitNumber', e.target.value)}
                        onFocus={() => setFocusedRowId(row.id)}
                        onBlur={() => setFocusedRowId(null)}
                      />
                    }
                  />
                }
                description={unitNumberDescription}
              />
            </Box>
            <Box>
              <FormField
                required={true}
                error={showBedsError}
                label={<Label css={{ md: { srOnly: true } }}>Beds</Label>}
                control={
                  <Select value={row.beds} onChange={(e) => handleFieldChange(row.id, 'beds', e.target.value)}>
                    {bedOptions.map((opt: string) => (
                      <option key={opt} value={opt}>
                        {opt || 'Select'}
                      </option>
                    ))}
                  </Select>
                }
                description={
                  showBedsError ? <FormHelp>Number of beds is required</FormHelp> : <DescriptionPlaceholder />
                }
              />
            </Box>
            <Box>
              <FormField
                required={true}
                error={showBathsError}
                label={<Label css={{ md: { srOnly: true } }}>Baths</Label>}
                control={
                  <Select value={row.baths} onChange={(e) => handleFieldChange(row.id, 'baths', e.target.value)}>
                    {bathOptions.map((opt: string) => (
                      <option key={opt} value={opt}>
                        {opt || 'Select'}
                      </option>
                    ))}
                  </Select>
                }
                description={
                  showBathsError ? <FormHelp>Number of bathrooms is required</FormHelp> : <DescriptionPlaceholder />
                }
              />
            </Box>
            <Box>
              <FormField
                optional={true}
                error={Boolean(sizeFieldMessage)}
                label={<Label css={{ md: { srOnly: true } }}>Size</Label>}
                control={
                  <AdornedInput
                    input={
                      <AdornedInput.Input
                        type="text"
                        inputMode="numeric"
                        value={row.size}
                        onChange={(e) => handleFieldChange(row.id, 'size', e.target.value)}
                      />
                    }
                    endAdornment={<AdornedInput.Adornment>sqft</AdornedInput.Adornment>}
                  />
                }
                description={sizeFieldMessage ? <FormHelp>{sizeFieldMessage}</FormHelp> : <DescriptionPlaceholder />}
              />
            </Box>
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'flex-start',
                gridColumn: { base: 'auto', md: 'span 2' },
              }}
            >
              <Box css={{ display: { base: 'block', md: 'none' }, marginBottom: 'tighter' }}>
                <Text textStyle="body-sm-bold">Options</Text>
              </Box>
              {renderRowActions ? (
                renderRowActions(row, isRowValid)
              ) : (
                <ButtonGroup aria-label="Unit options">
                  <Tooltip.Root>
                    <Tooltip.Trigger>
                      <IconButton
                        data-testid="copy-unit-button"
                        title="Duplicate"
                        size="md"
                        tone="neutral"
                        emphasis="bare"
                        icon={<IconCopyFilled />}
                        disabled={rows.length >= maxUnits}
                        onClick={() => handleCopyRow(row.id)}
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content>
                        Duplicate
                        <Tooltip.Arrow />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                  <Tooltip.Root>
                    <Tooltip.Trigger>
                      <IconButton
                        data-testid="remove-unit-button"
                        title="Remove"
                        size="md"
                        tone="critical"
                        emphasis="bare"
                        icon={<IconMinusCircleFilled />}
                        disabled={rows.length <= minRows}
                        onClick={() => handleRemoveRow(row.id)}
                      />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content>
                        Remove
                        <Tooltip.Arrow />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </ButtonGroup>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default EditableUnitGrid;
